import React, { useEffect, useState } from 'react';
import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import {
  DetailOption,
  QlfFilterDetails,
  QlfMasterDetail,
  QlfSimpleDetail,
} from '@qualifio/ui-commons/dist/components/qlf-master-detail';
import { Feature, ruleUsesCustomKey } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfConfirmationDialog } from '@qualifio/ui-commons';
import { Skeleton } from '@mui/material';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { CustomKeyDetail } from '../../libs/custom-keys/components/detail/CustomKeyDetail';
import { Page, PageBody, PageHeader } from '../../libs/shared/page';
import { QlfMasterDetailContainer } from '../../libs/shared/layout/QlfMasterDetailContainer';
import { QlfPageErrorBoundary } from '../../libs/shared/QlfPageErrorBoundary';
import { TopBar } from '../../libs/shared/layout/TopBar';
import { useAccountRules } from '../../libs/rules/AccountRulesContext';
import { useAccounts } from '../../libs/accounts/AccountsContext';
import { useConfirmDialog } from '../../libs/shared/use-confirm-dialog';
import { useCustomKeys } from '../../libs/custom-keys/CustomKeysContext';
import { useFeatures } from '../../libs/features/FeaturesContext';

const useStyles = makeStyles(() => ({
  deletedRule: {
    fontWeight: 600,
  },
  deletedRules: {
    marginTop: '20px',
  },
}));

export function CustomKeysIndexPage() {
  const classes = useStyles();
  const { t } = useTranslation();
  const { currentAccount } = useAccounts();
  const [searchField, setSearchField] = useState('');
  const { features } = useFeatures();
  const { remove: customKeyRemove, write: customKeyWrite } = features[Feature.CUSTOM_KEY];
  const { askConfirmation, props, setProps, ...dialogProps } = useConfirmDialog();
  const { accountRules, fetchAccountRules } = useAccountRules();

  const {
    selectedCustomKey,
    setSelectedCustomKey,
    getCustomKeys,
    customKeys,
    isLoading,
    createCustomKey,
    deleteCustomKey,
  } = useCustomKeys();

  useEffect(() => {
    fetchAccountRules(false);
    return () => {
      setSelectedCustomKey(undefined);
    };
  }, []);

  const templateHeader = () => (
    <QlfFilterDetails
      placeholder={t('custom-keys.sidebar.search')}
      onAdd={() => createCustomKey()}
      disableAdd={!customKeyWrite}
      onChange={e => setSearchField(e.target.value)}
    />
  );

  const templateRow = (props: { data: CustomKeyAttributes }) => {
    const { data: customKey } = props;

    const options: DetailOption[] = [
      {
        node: <p>{t('master-detail.actions.remove')}</p>,
        startIcon: <FontAwesomeIcon fixedWidth={true} icon={faTrash} />,
        callback: () => {
          setProps({ customKeyTechnicalName: customKey?.technicalName });
          askConfirmation(() => {
            if (customKey?.id) {
              deleteCustomKey(customKey.id);
            }
          });
        },
        disabled: !customKeyRemove,
        tooltip: customKeyRemove ? '' : t('shared.action.permission.required'),
      },
      {
        node: <p>{t('master-detail.actions.duplicate')}</p>,
        startIcon: <FontAwesomeIcon fixedWidth={true} icon={faCopy} />,
        callback: () => createCustomKey(customKey),
        disabled: !customKeyWrite,
        tooltip: customKeyWrite ? '' : t('shared.action.permission.required'),
      },
    ];

    return (
      <QlfSimpleDetail
        state={customKey.id === selectedCustomKey?.id ? 'active' : 'default'}
        options={options}
        callback={() => setSelectedCustomKey(props.data)}
        content={customKey.name}
      />
    );
  };

  const items = customKeys.filter(
    customKey => !searchField || customKey.name.includes(searchField),
  );

  useEffect(() => {
    getCustomKeys();
  }, [currentAccount.id]);

  return (
    <Page>
      <PageHeader>
        <TopBar />
      </PageHeader>
      <PageBody>
        <QlfPageErrorBoundary>
          {isLoading('getCustomKeys') ? (
            <Skeleton />
          ) : (
            <>
              <QlfMasterDetailContainer isAccountLevel>
                <QlfMasterDetail
                  templateHeader={templateHeader}
                  templateRow={templateRow}
                  templateDetail={CustomKeyDetail}
                  items={items}
                />
              </QlfMasterDetailContainer>
              <QlfConfirmationDialog
                {...dialogProps}
                title={t('custom-keys.confirm-delete.title')}
                content={
                  (
                    <>
                      {t('custom-keys.confirm-delete.message')}
                      <div className={classes.deletedRules}>
                        {accountRules.filter(rule =>
                          ruleUsesCustomKey(rule.criteria, props?.customKeyTechnicalName),
                        ).length ? (
                          <div>
                            {t('levels.confirm-delete.rules-warning')}
                            {accountRules
                              .filter(rule =>
                                ruleUsesCustomKey(rule.criteria, props?.customKeyTechnicalName),
                              )
                              .map(rule => (
                                <div key={rule.id} className={classes.deletedRule}>
                                  {`- ${rule.name}`}
                                </div>
                              ))}
                          </div>
                        ) : (
                          ''
                        )}
                      </div>
                    </>
                  ) as any
                }
                color={'warning'}
                confirmButtonText={t('generic.actions.hard-delete')}
                cancelButtonText={t('generic.actions.cancel')}
              />
            </>
          )}
        </QlfPageErrorBoundary>
      </PageBody>
    </Page>
  );
}
